import type { InsightGroup } from "@editor/infra/analytics";

import * as React from "react";

import { analyticsRouter } from "@editor/trpc/analytics";
import { isInsightBuildableInReplo } from "@editor/utils/insights";

import { FAKE_INSIGHT } from "@/features/analytics/fakeData";
import useAnalyticsStatus from "@/features/analytics/useAnalyticsStatus";
import { skipToken } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useAnalyticsQueryContext } from "./contexts/AnalyticsQueryContext";

const useAnalyticsInsights = (activeInsightGroup: InsightGroup) => {
  const { workspaceId, projectId } = useAnalyticsQueryContext();
  const { shouldBlurFullScreen } = useAnalyticsStatus();

  const rangeStartDate = startOfDay(addDays(new Date(), -7));

  const { data: allInsights, isLoading } = analyticsRouter.getInsights.useQuery(
    (workspaceId || projectId) && !shouldBlurFullScreen
      ? {
          workspaceId: workspaceId ?? undefined,
          projectId: projectId ?? undefined,
          rangeStart: rangeStartDate.valueOf(),
        }
      : skipToken,
  );

  const activeGroupInsights = allInsights?.filter((insight) => {
    return exhaustiveSwitch({ type: activeInsightGroup })({
      "build-in-replo": isInsightBuildableInReplo(insight),
      "trending-up": insight.sentiment === "positive",
      "trending-down": insight.sentiment === "negative",
      other: insight.sentiment === "neutral",
    });
  });

  if (shouldBlurFullScreen) {
    const dummyInsights = Array.from({ length: 4 }, () => FAKE_INSIGHT);

    return {
      activeGroupInsights: dummyInsights,
      allInsights: dummyInsights,
      isLoading: false,
    };
  }

  return {
    activeGroupInsights,
    allInsights,
    isLoading,
  };
};

export default useAnalyticsInsights;
